import { useEffect, useState } from "react";
import {
  Box, Button, Flex, FormControl, FormLabel, IconButton, Input, NumberInput, NumberInputField, Select, SimpleGrid,
  Switch, Text, Textarea, useToast,
} from "@chakra-ui/react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { crm } from "../../../theme";
import {
  getSettlementSettings, saveSettlementSettings, SettlementIssuer, SettlementSettings as Model,
} from "../../../services/settlementService";

const companyNames: Record<number, string> = { 1: "CVF Logística", 2: "CVF Distribuidora", 3: "CVF Rondônia", 4: "W Trading" };
const title = { fontSize: "13px", fontWeight: "700", color: crm.text, mb: "8px", mt: "24px" };
const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

/** Configuração da prestação de contas. Admin edita; demais só leem. */
const SettlementSettings = () => {
  const toast = useToast();
  const auth = useAuthUser<any>();
  const isAdmin = Boolean(auth?.admin);
  const [form, setForm] = useState<Model | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettlementSettings().then(setForm).catch(() => toast({ status: "error", title: "Não foi possível carregar a configuração" }));
  }, []);

  if (!form) return null;

  const set = (patch: Partial<Model>) => setForm({ ...form, ...patch });
  const setIssuer = (type: number, patch: Partial<SettlementIssuer>) =>
    set({ issuers: form.issuers.map((i) => (i.processType === type ? { ...i, ...patch } : i)) });
  const setEmail = (patch: Partial<Model["email"]>) => set({ email: { ...form.email, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      setForm(await saveSettlementSettings(form));
      toast({ status: "success", title: "Configuração salva" });
    } catch (e: any) {
      toast({ status: "error", title: "Não foi possível salvar", description: e?.message, duration: 10000, isClosable: true });
    } finally { setSaving(false); }
  };

  const issuerField = (i: SettlementIssuer, key: keyof SettlementIssuer, label: string) => (
    <FormControl>
      <Label>{label}</Label>
      <Input size="sm" isReadOnly={!isAdmin} value={(i[key] as string) ?? ""} onChange={(e) => setIssuer(i.processType, { [key]: e.target.value })} />
    </FormControl>
  );

  return (
    <Box bg={crm.surface} border={`1px solid ${crm.border}`} borderRadius={crm.radius} boxShadow={crm.shadowSm} p="24px">
      <Text fontSize="18px" fontWeight="700" color={crm.text}>Prestação de contas</Text>
      <Text fontSize="13px" color={crm.textMuted}>
        Categorias dos lançamentos, dados da empresa no demonstrativo e o e-mail enviado ao cliente.{!isAdmin && " Só administradores podem alterar."}
      </Text>

      <Text {...title}>Categorias</Text>
      <Box border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm} p="12px">
        {form.categories.map((c, idx) => (
          <Flex key={idx} gap="8px" mb="6px" align="center">
            <Input size="sm" isReadOnly={!isAdmin} value={c.name}
              onChange={(e) => set({ categories: form.categories.map((x, j) => (j === idx ? { ...x, name: e.target.value } : x)) })} />
            <Select size="sm" w="180px" isDisabled={!isAdmin} value={c.kind}
              onChange={(e) => set({ categories: form.categories.map((x, j) => (j === idx ? { ...x, kind: e.target.value as any } : x)) })}>
              <option value="expense">Despesa</option>
              <option value="advance">Adiantamento</option>
            </Select>
            {isAdmin && (
              <IconButton aria-label="Remover categoria" size="sm" variant="ghost" icon={<FaRegTrashAlt />}
                onClick={() => set({ categories: form.categories.filter((_, j) => j !== idx) })} />
            )}
          </Flex>
        ))}
        {isAdmin && (
          <Button size="sm" variant="ghost" leftIcon={<FaPlus />} onClick={() => set({ categories: [...form.categories, { name: "", kind: "expense" }] })}>
            Adicionar categoria
          </Button>
        )}
      </Box>

      <Text {...title}>Empresas (cabeçalho e dados bancários do demonstrativo)</Text>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="12px">
        {form.issuers.slice().sort((a, b) => a.processType - b.processType).map((i) => (
          <Box key={i.processType} border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm} p="12px">
            <Text fontSize="13px" fontWeight="700" mb="8px">{companyNames[i.processType] ?? `Tipo ${i.processType}`}</Text>
            <SimpleGrid columns={2} spacing="8px">
              {issuerField(i, "name", "Razão social")}
              {issuerField(i, "cnpj", "CNPJ")}
              {issuerField(i, "street", "Endereço")}
              {issuerField(i, "zipCity", "CEP / cidade")}
              {issuerField(i, "bank", "Banco")}
              {issuerField(i, "agency", "Agência")}
              {issuerField(i, "account", "Conta corrente")}
              {issuerField(i, "pix", "Chave PIX")}
              {issuerField(i, "beneficiary", "Favorecido")}
            </SimpleGrid>
          </Box>
        ))}
      </SimpleGrid>

      <Text {...title}>E-mail ao cliente</Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
        <FormControl display="flex" alignItems="center" gap="8px">
          <Switch isDisabled={!isAdmin} isChecked={form.email.toClientContacts} onChange={(e) => setEmail({ toClientContacts: e.target.checked })} />
          <Text fontSize="13px">Preencher "Para" com os contatos do cliente</Text>
        </FormControl>
        <FormControl>
          <Label>Cópia padrão (separe por ;)</Label>
          <Input size="sm" isReadOnly={!isAdmin} value={form.email.cc ?? ""} onChange={(e) => setEmail({ cc: e.target.value })} />
        </FormControl>
        <FormControl>
          <Label>Vencimento da cobrança (dias úteis)</Label>
          <NumberInput size="sm" min={0} max={60} isReadOnly={!isAdmin} value={form.email.dueDays} onChange={(_, n) => setEmail({ dueDays: Number.isNaN(n) ? 0 : n })}>
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </SimpleGrid>
      <FormControl mt="12px">
        <Label>Assunto</Label>
        <Input size="sm" isReadOnly={!isAdmin} value={form.email.subject} onChange={(e) => setEmail({ subject: e.target.value })} />
      </FormControl>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="12px" mt="12px">
        <FormControl>
          <Label>Mensagem quando há saldo a cobrar (HTML)</Label>
          <Textarea size="sm" rows={8} isReadOnly={!isAdmin} value={form.email.bodyCharge} onChange={(e) => setEmail({ bodyCharge: e.target.value })} />
        </FormControl>
        <FormControl>
          <Label>Mensagem quando há saldo a devolver (HTML)</Label>
          <Textarea size="sm" rows={8} isReadOnly={!isAdmin} value={form.email.bodyRefund} onChange={(e) => setEmail({ bodyRefund: e.target.value })} />
        </FormControl>
      </SimpleGrid>
      <Text fontSize="11px" color={crm.textFaint} mt="6px">
        Variáveis: {"{ref}"}, {"{refCliente}"}, {"{cliente}"}, {"{empresa}"}, {"{saldo}"}, {"{vencimento}"}, {"{dadosBancarios}"}, {"{usuario}"}. Use &lt;br&gt; para quebrar linha.
      </Text>

      {isAdmin && (
        <Flex justify="flex-end" mt="24px">
          <Button colorScheme="blue" onClick={save} isLoading={saving}>Salvar configuração</Button>
        </Flex>
      )}
    </Box>
  );
};

export default SettlementSettings;
